import { ResourceClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BOM_STATUSES,
  dimensionLabel,
  INVENTORY_POLICIES,
  PART_STATUSES,
  PART_TYPES,
  PartGridClient,
  type DepartmentDTO,
  type PartGridDTO,
  type PartGroupDTO,
  type UnitDTO,
} from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { bomStatusLabel } from "./PartBomTab";
import { inventoryPolicyLabel, partStatusLabel, partTypeLabel } from "./PartGeneralTab";

/**
 * The part master, a page at a time.
 *
 * <p>Sorting, filtering and search happen in the database: the whole list was
 * 14 MB, and a grid that downloads it spends its first seconds on rows nobody
 * scrolls to. Each column id is the field the server publishes — that pairing
 * is the contract, and a column the server does not know is a 400 rather than
 * a filter that silently does nothing.</p>
 *
 * <p>Inactive parts are one filter away rather than a separate screen: they are
 * what gets looked up when a number turns up on an old drawing.</p>
 */
export function ManufacturingPartsPage() {
  const { hasView } = useAuth();
  return hasView(NexpimView.MANUFACTURING_PARTS) ? <ManufacturingPartsContent /> : <AccessDenied />;
}

/** Shown in place of a hidden page reached by its URL (the SIGM MFI replacement is dark; docs/sigm-mfi-replacement/dark-launch.md). */
function AccessDenied() {
  const { t } = useTranslation();
  return (
    <div className="p-4 lg:p-6">
      <p className="text-[13px] text-ink-muted">
        {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
      </p>
    </div>
  );
}

function ManufacturingPartsContent() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();

  const client = useMemo(() => new PartGridClient(http), [http]);

  const parts = useServerTable<PartGridDTO>({
    queryKey: ["manufacturing", "part-grid"],
    fetchPage: (request) => client.find(request),
    fetchTotal: () => client.count(),
  });

  // Select filters must list their options: in server mode the loaded page is
  // not a complete sample of the values, so options cannot be read off it.
  const reference = { staleTime: 30 * 60_000 };
  const groups = useQuery({
    queryKey: ["manufacturing-part-group", "all"],
    queryFn: () => new ResourceClient<PartGroupDTO>(http, "/manufacturing/part-group").findAll(),
    ...reference,
  });
  const units = useQuery({
    queryKey: ["unit", "all"],
    queryFn: () => new ResourceClient<UnitDTO>(http, "/unit").findAll(),
    ...reference,
  });
  const departments = useQuery({
    queryKey: ["department", "all"],
    queryFn: () => new ResourceClient<DepartmentDTO>(http, "/department").findAll(),
    ...reference,
  });

  const cols = useMemo(() => {
    const c = defineColumns<PartGridDTO>();
    const options = (values: readonly string[], label: (value: string, translate: typeof t) => string) =>
      values.map((value) => ({ value, label: label(value, t) }));
    return [
      c.text("number", (row) => row.number, { headerKey: "column.part.number", priority: 1 }),
      c.text("name", (row) => row.name, { headerKey: "column.part.name", priority: 2 }),
      c.custom(
        "part_group_code",
        (row) => row.part_group_code ?? "",
        (row) =>
          row.part_group_code ? (
            <span>
              {row.part_group_code}
              <span className="ml-1 text-[11px] text-ink-faint">{row.part_group_name}</span>
            </span>
          ) : (
            <span className="text-ink-faint"></span>
          ),
        {
          headerKey: "column.part.group",
          filter: "multiselect",
          filterOptions: (groups.data ?? []).map((group) => ({ value: group.code, label: `${group.code} · ${group.name}` })),
          priority: 3,
        },
      ),
      c.custom(
        "type",
        (row) => row.type,
        (row) => (
          <Badge variant={row.type === "MANUFACTURED" ? "info" : row.type === "VIRTUAL" ? "warning" : "neutral"} size="sm">
            {partTypeLabel(row.type, t)}
          </Badge>
        ),
        { headerKey: "column.part.type", filter: "select", filterOptions: options(PART_TYPES, partTypeLabel), priority: 3 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <Badge variant={row.status === "ACTIVE" ? "success" : "error"} size="sm">
            {partStatusLabel(row.status, t)}
          </Badge>
        ),
        { headerKey: "column.part.status", filter: "select", filterOptions: options(PART_STATUSES, partStatusLabel) },
      ),
      c.number("on_hand", (row) => row.on_hand, { headerKey: "column.part.on-hand", filter: "range", priority: 3 }),
      // One column, not three: a panel is "1570 × 345 × 16 mm" to everyone who
      // works with it. It sorts by length, the dimension read first.
      c.text("size", (row) => dimensionLabel(row), { headerKey: "column.part.size", filter: "none", priority: 4 }),
      c.number("weight_g", (row) => row.weight_g, { headerKey: "column.part.weight", filter: "range", defaultVisible: false }),
      c.text("stock_unit_code", (row) => row.stock_unit_code ?? "", {
        headerKey: "column.part.unit",
        filter: "select",
        filterOptions: (units.data ?? []).map((unit) => ({ value: unit.code, label: unit.code })),
      }),
      c.custom(
        "bom_status",
        (row) => row.bom_status ?? "NONE",
        (row) =>
          row.bom_status === null ? (
            <span className="text-ink-faint"></span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Badge
                variant={row.bom_status === "ACTIVE" ? "success" : row.bom_status === "DRAFT" ? "warning" : "neutral"}
                size="sm"
              >
                {bomStatusLabel(row.bom_status, t)}
              </Badge>
              {row.bom_line_count > 0 && (
                <span className="text-[11px] text-ink-faint">
                  {t("part.bom.components", "{{n}} comp.", { n: row.bom_line_count })}
                </span>
              )}
            </span>
          ),
        {
          headerKey: "column.part.bom",
          filter: "multiselect",
          filterOptions: [...options(BOM_STATUSES, bomStatusLabel), { value: "NONE", label: t("part.bom.none", "None") }],
          priority: 4,
        },
      ),
      c.text("department_code", (row) => row.department_code ?? "", {
        headerKey: "column.part.department",
        filter: "select",
        filterOptions: (departments.data ?? []).map((department) => ({
          value: department.code,
          label: `${department.code} · ${department.name}`,
        })),
        defaultVisible: false,
      }),
      c.text("drawing_number", (row) => row.drawing_number ?? "", {
        headerKey: "column.part.drawing",
        defaultVisible: false,
      }),
      c.number("supplier_count", (row) => row.supplier_count, {
        headerKey: "column.part.suppliers",
        filter: "range",
        defaultVisible: false,
      }),
      c.number("lead_time_days", (row) => row.lead_time_days, {
        headerKey: "column.part.lead-time",
        filter: "range",
        defaultVisible: false,
      }),
      c.custom(
        "inventory_policy",
        (row) => row.inventory_policy,
        (row) => <span className="text-[11px] text-ink-muted">{inventoryPolicyLabel(row.inventory_policy, t)}</span>,
        {
          headerKey: "column.part.policy",
          filter: "select",
          filterOptions: options(INVENTORY_POLICIES, inventoryPolicyLabel),
          defaultVisible: false,
        },
      ),
    ];
  }, [t, groups.data, units.data, departments.data]);

  return (
    <>
      <PageHeader
        title={t("navigation.item.manufacturing-parts", "Parts")}
        subtitle={t("manufacturing.parts.subtitle", "The part master: everything the plant buys, makes or consumes.")}
      />

      <DataTable<PartGridDTO>
        data={parts.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/manufacturing/parts"
        loading={parts.loading}
        server={parts.mode}
        // Inactive parts stay one click away; they are just not what anyone
        // opens this page to see.
        initialFilters={[{ id: "status", value: ["ACTIVE"] }]}
        onRowClick={(row) =>
          void navigate({ to: "/manufacturing/parts/$partId", params: { partId: String(row.id) } })
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
